"use client";

import { c as _c } from "react/compiler-runtime";
import { useSuspenseQuery } from "@apollo/client/react";
import { FaqType, GET_FAQS } from "@/graphql/queries/faqs-query";
import { forwardRef, useImperativeHandle } from "react";
import { useFaqParams } from "@/hooks/useFaqParams";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { LoadMore } from "./load-more";
import { Heading } from "../ui/heading";
import { Text } from "../ui/text";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../ui/empty";
import { ListX } from "lucide-react";
type FaqsProps = {
  heading?: string;
};
export type FaqsRef = {
  refetch: (variables?: {
    faqType: FaqType;
  }) => Promise<any>;
};
export const Faqs = forwardRef<FaqsRef, FaqsProps>((t0, ref) => {
  const $ = _c(14);
  const {
    faqType,
    slug,
    country,
    city
  } = useFaqParams();
  let t1;
  if ($[0] !== city || $[1] !== country || $[2] !== faqType || $[3] !== slug) {
    t1 = {
      variables: {
        faqType,
        slug,
        country,
        city
      }
    };
    $[0] = city;
    $[1] = country;
    $[2] = faqType;
    $[3] = slug;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data,
    error,
    refetch
  } = useSuspenseQuery(GET_FAQS, t1);
  let t2;
  if ($[5] !== refetch) {
    t2 = () => ({
      refetch: variables => refetch(variables)
    });
    $[5] = refetch;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useImperativeHandle(ref, t2);
  if (error) {
    console.error("Error fetching FAQs:", error);
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div>Error loading FAQs. Please try again later.</div>;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (data?.faqs?.length === 0) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Empty className="border border-dashed"><EmptyHeader><EmptyMedia variant="icon"><ListX /></EmptyMedia><EmptyTitle>{"FAQ's Empty"}</EmptyTitle><EmptyDescription>No FAQs available for the selected category.</EmptyDescription></EmptyHeader></Empty>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Accordion />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== data?.faqs) {
    t4 = data?.faqs || [];
    $[10] = data?.faqs;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <LoadMore initialVisibleItems={7} wrapper={t3} items={t4} renderItems={_temp} />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
});
Faqs.displayName = "Faqs";
function _temp(item, index) {
  return <AccordionItem key={`faq-${index}`} render={<div />}><AccordionTrigger nativeButton={false} render={<Heading as="h3" render={<div className="text-wrap" />}>{item.question}</Heading>} /><AccordionContent render={<Text className="typeset">{item.answer}</Text>} /></AccordionItem>;
}